/**
 * La scheda di una fattura sul computer, ridisegnata il 26/10/2026
 * (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: questa fattura è pagata? Se no, quanto manca, da quando, e cosa faccio adesso?
 * La forma: un documento con la risposta in una frase in cima, le rate come una lista da
 * spuntare (ognuna col suo "Segna incassata"), una sola azione principale; accanto il cliente,
 * il percorso della fattura spiegato in parole e le altre azioni con una riga che dice cosa fanno.
 *
 * Prima: numero gigante, il ciclo Bozza/Emessa/Inviata/Pagata a quattro pallini senza
 * spiegazione, le rate che non si vedevano e "Segna incassata" che chiudeva tutte le rate
 * SENZA CONFERMA e mandava la mail vera. Ora la conferma dice quali rate chiude, con che
 * data e che parte la mail (incasso.tsx, lo stesso testo del telefono).
 * Sul telefono la scheda è FatturaMobile.
 */
import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { useAuthStore } from '../../stores/auth.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { quotesQueryOptions } from '../../lib/scadenziario';
import { fmtEur } from '../../lib/utils';
import { VISTA_LABEL, adminLinks } from '../../lib/adminRoutes';
import { canReadAdmin } from '../admin/adminApi';
import { SettlePopover } from '../admin/SettlePopover';
import { durata, giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, nomeBreve } from '../admin/fatture/fattureApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useDocActions } from './useDocActions';
import { FatturaMobile } from './FatturaMobile';
import { AVVISO_INCASSO, TestoConfermaIncasso, rateVive, scadenzaRata, type RataFattura } from './incasso';
import './fattura.css';

interface InvoiceFull {
  id: string;
  number: string | null;
  type: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  subtotal: string;
  vat: string;
  total: string;
  paymentMethod: string | null;
  sdiId: string | null;
  notes: string | null;
  ficDocumentId: number | null;
  ficUrl: string | null;
  ficPushedAt: string | null;
  ficSyncedAt: string | null;
  client: { id: string; legalName: string; shortName: string; vat: string | null; sdiCode: string | null; pec: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string }>;
  /** Le rate (GET /invoices/:id le manda già: id, seq, importo, stato, scadenza, incasso). */
  payments?: RataFattura[];
}

interface FicConnectionStatus {
  connected: boolean;
  companyName: string | null;
}

/** Il tipo di documento con parole intere (era lo slug: "nota credito", "avviso pagamento"). */
const TIPO_FATTURA: Record<string, string> = {
  FATTURA: 'Fattura',
  NOTA_CREDITO: 'Nota di credito',
  PROFORMA: 'Pre-fattura',
  AVVISO_PAGAMENTO: 'Avviso di pagamento',
};

const euro = (n: number | string) => `€ ${fmtEur(Number(n), true)}`;

/** Il percorso di una fattura, ogni passo con la frase che dice cosa vuol dire. */
const PASSI: Array<{ key: string; titolo: string; spiega: (onFic: boolean) => string }> = [
  { key: 'bozza', titolo: 'Bozza', spiega: () => 'La prepariamo noi: il cliente non la vede e non ha ancora un numero.' },
  { key: 'emessa', titolo: 'Emessa', spiega: (fic) => (fic ? 'Ha il numero ufficiale e il PDF, dati da FattureInCloud.' : 'Ha il numero di Masamune OS.') },
  { key: 'inviata', titolo: 'Mandata al cliente', spiega: () => "Il cliente l'ha ricevuta e deve pagarla entro la scadenza." },
  { key: 'pagata', titolo: 'Pagata', spiega: () => 'Tutte le rate sono incassate: la fattura è chiusa.' },
];

/**
 * Sul telefono la scheda è FatturaMobile (in cima quanto e se è pagata, il
 * gesto che serve in fondo, il resto nel menu ⋯). Sul computer resta questa.
 */
export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  if (isMobile && id) return <div className="page"><FatturaMobile id={id} /></div>;
  return <InvoiceDetailDesktop />;
}

function InvoiceDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [notes, setNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  /** La rata col popover "Segna incassata" aperto ('tutta' = la fattura senza rate). */
  const [incassoAperto, setIncassoAperto] = useState<string | null>(null);
  const { markInvoicePaid } = useDocActions();

  const lvDocs = (user?.permissions.find((p) => p.module === ModuleKey.FATTURAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
  const canWrite = hasAtLeast(lvDocs, PermissionLevel.WRITE);
  // Movimenti in banca e conti sono dell'Amministrazione: senza, il popover resta con data, modo e nota.
  const conBanca = canReadAdmin(user);

  const { data: inv, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<InvoiceFull>(`/invoices/${id}`),
    enabled: !!id,
    retry: false,
  });

  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicConnectionStatus>('/settings/integrations/fic/status'),
    refetchOnWindowFocus: false,
  });

  // Preventivi: stessa cache della lista. Serve a dire da quale preventivo è nata la fattura.
  const { data: quoteData } = useQuery({ ...quotesQueryOptions, enabled: !!id });

  // ──── Mutations
  const issue = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Fattura emessa con la numerazione interna', 'Stato aggiornato e numero assegnato.');
    },
    onError: (e: any) => toast.alert('Fattura non emessa', e?.message ?? 'Riprova'),
  });

  const updateNotes = useMutation({
    mutationFn: () => api.patch(`/invoices/${id}`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      setNotes('');
      setNotesOpen(false);
      toast.ok('Nota interna aggiornata');
    },
    onError: (e: any) => toast.alert('Nota interna non salvata', e?.message ?? 'Riprova'),
  });

  const pushToFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/push-to-fic`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      // Pushare a FIC cambia status BOZZA → EMESSA: il fatturato della dashboard lo conta.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Fattura emessa su FattureInCloud', 'Il numero ufficiale lo ha dato FattureInCloud.');
    },
    onError: (e: any) => toast.alert('Fattura non emessa su FattureInCloud', e?.message ?? 'Riprova'),
  });

  const syncFromFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/sync-from-fic`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.ok('Fattura aggiornata da FattureInCloud');
    },
    onError: (e: any) => toast.alert('Fattura non aggiornata da FattureInCloud', e?.message ?? 'Riprova'),
  });

  const sendEmailFic = useMutation({
    mutationFn: (payload: { to?: string; cc?: string; subject?: string; body?: string }) =>
      api.post(`/invoices/${id}/send-email-fic`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      setEmailModalOpen(false);
      toast.ok('Fattura mandata al cliente per email', 'La mail è partita da FattureInCloud.');
    },
    onError: (e: any) => toast.alert('Fattura non mandata al cliente', e?.message ?? 'Riprova'),
  });

  const sendSdi = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/send-sdi`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.ok("Fattura mandata all'Agenzia delle Entrate");
    },
    onError: (e: any) => toast.alert("Fattura non mandata all'Agenzia delle Entrate", e?.message ?? 'Riprova'),
  });

  // ──── States
  if (!id) return <Navigate to={adminLinks.documenti()} replace />;
  if (isError) return (
    <div className="page invoice-detail-page">
      <Breadcrumb items={[{ label: VISTA_LABEL.fatture.documenti, to: adminLinks.documenti() }, { label: 'Fattura' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Fattura non trovata</h1>
        <div className="mute mt-2">Questa fattura non esiste o è stata cancellata.</div>
      </div>
    </div>
  );
  if (isLoading || !inv) return <DetailSkeleton header="chips" />;

  const ficConnected = !!ficStatus?.connected;
  const onFic = !!inv.ficDocumentId;
  const isDraft = inv.status === 'BOZZA';
  const isPaid = inv.status === 'PAGATA';
  const isCancelled = inv.status === 'ANNULLATA';
  const aperta = ['EMESSA', 'INVIATA', 'SCADUTA'].includes(inv.status);
  const tipo = TIPO_FATTURA[inv.type] ?? inv.type;
  const chi = nomeBreve(inv.client.shortName || inv.client.legalName);

  // Tutte le rate si mostrano (anche quelle tolte da una nota di credito, se no i conti
  // non tornano col totale); si contano solo le vive.
  const tutte = inv.payments ?? [];
  const vive = rateVive(tutte);
  const aperte = vive.filter((p) => p.status === 'DA_INCASSARE');
  const scadenzaAperta = aperte[0]?.dueDate ?? inv.dueDate;
  const giorni = giorniDaOggi(scadenzaAperta);
  const inRitardo = aperta && giorni != null && giorni < 0;
  const label = `${tipo} ${inv.number ?? 'senza numero'} · ${chi}`;
  const residuo = aperte.length ? aperte.reduce((s, p) => s + Number(p.amount), 0) : Number(inv.total);

  // Il percorso: 0 bozza · 1 emessa · 2 mandata al cliente · 3 pagata
  const passoAttuale = isDraft ? 0 : isPaid ? 3 : inv.status === 'INVIATA' || inv.status === 'SCADUTA' ? 2 : 1;

  const quoteRef = quoteData?.items.find((q) => q.convertedToInvoice?.id === inv.id) ?? null;

  // "Segna incassata" sulla fattura intera: chiede conferma e dice cosa chiude (26/10/2026).
  const segnaIncassataTutta = async () => {
    const ok = await confirmDialog({
      title: aperte.length > 1 ? `Segnare incassate ${aperte.length} rate della fattura?` : 'Segnare incassata la fattura?',
      body: <TestoConfermaIncasso payments={inv.payments} totale={inv.total} computer={vive.length > 0} />,
      confirmLabel: aperte.length > 1 ? 'Sì, incassate oggi' : 'Sì, incassata oggi',
      icon: 'check',
    });
    if (ok) markInvoicePaid.mutate({ id: inv.id, total: residuo, label });
  };

  // ──── L'azione che serve adesso: una sola.
  let adesso: { key: string; titolo: string; spiega: string; cta: string; icon: IconName; onClick: () => void; pending?: boolean; grave?: boolean } | null = null;
  if (canWrite && isDraft) {
    adesso = ficConnected
      ? { key: 'push-fic', titolo: 'Emettila su FattureInCloud', spiega: 'Le dà il numero ufficiale e il PDF.', cta: 'Emetti su FattureInCloud', icon: 'send', onClick: () => pushToFic.mutate(), pending: pushToFic.isPending }
      : { key: 'issue', titolo: 'Emettila qui', spiega: 'FattureInCloud non è collegato: prende il numero di Masamune OS.', cta: 'Emetti la fattura qui', icon: 'send', onClick: () => issue.mutate(), pending: issue.isPending };
  } else if (canWrite && aperta && onFic && inRitardo) {
    adesso = { key: 'send-email', grave: true, titolo: `In ritardo da ${durata(giorni!)}: rimandagli la fattura`, spiega: 'Riparte la mail da FattureInCloud, con il PDF.', cta: 'Rimanda la fattura', icon: 'mail', onClick: () => setEmailModalOpen(true) };
  } else if (canWrite && inv.status === 'EMESSA' && onFic) {
    adesso = { key: 'send-email', titolo: 'Mandala al cliente', spiega: 'Parte una mail da FattureInCloud con il PDF.', cta: 'Manda al cliente', icon: 'mail', onClick: () => setEmailModalOpen(true) };
  } else if (canWrite && aperta) {
    adesso = {
      key: 'mark-paid', grave: inRitardo,
      titolo: inRitardo ? `In ritardo da ${durata(giorni!)}: quando paga, segnala incassata` : 'Quando il cliente paga, segnala incassata',
      spiega: aperte.length > 1 ? `Chiude le ${aperte.length} rate aperte con la data di oggi, dopo una conferma.` : 'Si chiude con la data di oggi, dopo una conferma.',
      cta: 'Segna incassata', icon: 'check', onClick: segnaIncassataTutta, pending: markInvoicePaid.isPending,
    };
  }

  // ──── Le azioni rare: nel menu ⋯ della testata (26/10/2026, "alleggerire": niente elenco in vista).
  // Le etichette dicono cosa fanno; quella che non si annulla (SdI) chiede conferma.
  const altre: ActionItem[] = [
    { key: 'send-email', icon: 'mail', label: 'Manda la fattura al cliente per email', onClick: () => setEmailModalOpen(true), hidden: !onFic || !canWrite || isCancelled },
    { key: 'mark-paid', icon: 'check', label: 'Segna incassate tutte le rate, con la data di oggi', onClick: segnaIncassataTutta, hidden: !canWrite || !aperta || aperte.length < 2 },
    { key: 'open-fic-pdf', icon: 'link', label: 'Apri il PDF su FattureInCloud', onClick: () => window.open(inv.ficUrl!, '_blank'), hidden: !inv.ficUrl },
    { key: 'sync-fic', icon: 'refresh', label: 'Rileggi la fattura da FattureInCloud', onClick: () => syncFromFic.mutate(), hidden: !onFic || !canWrite },
    {
      key: 'send-sdi', icon: 'send', label: "Manda all'Agenzia delle Entrate (fattura elettronica)", hidden: !onFic || !canWrite || isCancelled,
      onClick: async () => {
        if (await confirmDialog({
          title: "Mandare la fattura all'Agenzia delle Entrate?",
          body: 'Parte la fattura elettronica verso il Sistema di Interscambio (SdI) e non si può ritirare.',
          danger: true,
          confirmLabel: 'Manda la fattura',
        })) sendSdi.mutate();
      },
    },
    { key: 'issue', icon: 'send', label: 'Emetti la fattura qui, senza FattureInCloud', onClick: () => issue.mutate(), hidden: !(isDraft && !ficConnected && canWrite) },
    { key: 'settings', icon: 'settings', label: 'Collega FattureInCloud dalle Impostazioni', onClick: () => navigate('/settings'), hidden: ficConnected },
    {
      key: 'print', icon: 'export', label: 'Stampa la fattura o salvala in PDF',
      onClick: () => {
        window.print();
        toast.info('Stampa avviata', 'Usa "Salva come PDF" dal dialogo di stampa per esportare il documento.');
      },
    },
  ];
  const occupato = syncFromFic.isPending || markInvoicePaid.isPending || sendEmailFic.isPending || sendSdi.isPending || issue.isPending || pushToFic.isPending;

  return (
    <div className="page fd-page">
      <Breadcrumb items={[{ label: VISTA_LABEL.fatture.documenti, to: adminLinks.documenti() }, { label: inv.number ?? 'Bozza' }]} />

      {/* ── 1. La risposta ─────────────────────────────────────────────── */}
      <header className="fd-hd">
        <SectionMark icon="invoice" className="fd-sez" />
        <div className="min-w-0">
          <div className="fd-kicker">
            {tipo}{inv.issueDate ? <> emessa {ilGiorno(inv.issueDate)}</> : null}
          </div>
          <h1 className="fd-title">{isDraft && !inv.number ? `Bozza di ${tipo.toLowerCase()}` : `${tipo} ${inv.number ?? ''}`}</h1>
          <p className="fd-risposta">{risposta({ inv, chi, vive, aperte, residuo, giorni, scadenzaAperta })}</p>
        </div>
        <div className="fd-hd-azioni">
          <ActionsMenu variant="ghost" label="Altre azioni sulla fattura" items={altre} excludeKey={adesso?.key} disabled={occupato} />
        </div>
      </header>

      <div className="fd-cols">
        <div className="fd-main">
          {/* ── 2. Cosa fare adesso ──────────────────────────────────────── */}
          {isCancelled ? (
            <section className="fd-adesso">
              <span className="fd-adesso-ico"><Icon name="x" size={18} /></span>
              <span className="min-w-0">
                <span className="fd-adesso-t">Fattura annullata</span>
                <span className="fd-adesso-s">Non vale più e non si può modificare. Se serviva, c'è una nota di credito collegata.</span>
              </span>
            </section>
          ) : adesso && (
            <section className={`fd-adesso${adesso.grave ? ' is-grave' : ''}`} aria-label="Cosa fare adesso">
              <span className="fd-adesso-ico"><Icon name={adesso.icon} size={18} /></span>
              <span className="min-w-0">
                <span className="fd-adesso-t">{adesso.titolo}</span>
                <span className="fd-adesso-s">{adesso.spiega}</span>
              </span>
              <button className="btn btn-primary" disabled={adesso.pending || occupato} onClick={adesso.onClick}>
                <Icon name={adesso.icon} size={12} /> {adesso.pending ? 'Attendi…' : adesso.cta}
              </button>
            </section>
          )}

          {/* ── 3. Le rate ──────────────────────────────────────────────── */}
          {!isDraft && !isCancelled && (
            <section className="fd-blocco">
              <h2 className="fd-h2"><Icon name="calendarClock" size={15} /> {tutte.length > 1 ? `Le ${tutte.length} rate della fattura` : 'Il pagamento della fattura'}</h2>
              <div className="fd-rate">
                {tutte.length > 0 ? tutte.map((p) => {
                  const pagata = p.status === 'INCASSATA';
                  const stornata = p.status === 'STORNATA';
                  const g = giorniDaOggi(p.dueDate);
                  const tardi = !pagata && !stornata && g != null && g < 0;
                  const ultima = !pagata && !stornata && aperte.length === 1;
                  return (
                    <div key={p.id} className={`fd-rata${pagata ? ' is-pagata' : ''}${stornata ? ' is-stornata' : ''}${tardi ? ' is-tardi' : ''}`}>
                      <span className="fd-rata-ico" aria-hidden>{pagata ? <Icon name="check" size={13} /> : stornata ? <Icon name="x" size={12} /> : tardi ? <Icon name="alert" size={13} /> : null}</span>
                      <span className="min-w-0">
                        <span className="fd-rata-t">{tutte.length > 1 ? `Rata ${p.seq} di ${tutte.length}` : 'Unico pagamento'}</span>
                        <span className={`fd-rata-s${tardi ? ' is-alert' : ''}`}>
                          {pagata
                            ? <>incassata{p.paidDate ? ` ${ilGiorno(p.paidDate)}` : ''}</>
                            : stornata ? <>tolta da una nota di credito: non si incassa</>
                            : tardi ? <>{scadenzaRata(p.dueDate)}: in ritardo da {durata(g!)}</> : scadenzaRata(p.dueDate)}
                        </span>
                      </span>
                      <span className="fd-rata-cifra">{euro(p.amount)}</span>
                      <span className="fd-rata-act">
                        {!pagata && !stornata && canWrite && (
                          <span className="relative">
                            <button className="btn btn-sm" disabled={markInvoicePaid.isPending} onClick={() => setIncassoAperto(incassoAperto === p.id ? null : p.id)}
                              title="Registra l'incasso di questa rata: scegli la data, il modo e il bonifico">
                              <Icon name="check" size={10} /> Segna incassata
                            </button>
                            {incassoAperto === p.id && (
                              <SettlePopover
                                mode="incassa"
                                amount={Number(p.amount)}
                                subtitle={tutte.length > 1 ? `rata ${p.seq} di ${tutte.length} della fattura ${inv.number ?? ''}` : `fattura ${inv.number ?? ''}`}
                                target={conBanca ? { kind: 'INVOICE_PAYMENT', id: p.id } : undefined}
                                counterparty={inv.client.legalName}
                                side="CREDIT"
                                adminData={conBanca}
                                withNote
                                avviso={ultima ? `È l'ultima rata da incassare. ${AVVISO_INCASSO}` : undefined}
                                pending={markInvoicePaid.isPending}
                                onClose={() => setIncassoAperto(null)}
                                onConfirm={(v) => {
                                  setIncassoAperto(null);
                                  markInvoicePaid.mutate({ id: inv.id, total: Number(p.amount), paymentId: p.id, label, ...v });
                                }}
                              />
                            )}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                }) : (
                  // Fattura senza rate (emessa solo qui): un pagamento unico, la scadenza della fattura.
                  <div className={`fd-rata${isPaid ? ' is-pagata' : ''}${inRitardo ? ' is-tardi' : ''}`}>
                    <span className="fd-rata-ico" aria-hidden>{isPaid ? <Icon name="check" size={13} /> : inRitardo ? <Icon name="alert" size={13} /> : null}</span>
                    <span className="min-w-0">
                      <span className="fd-rata-t">Unico pagamento</span>
                      <span className={`fd-rata-s${inRitardo ? ' is-alert' : ''}`}>
                        {isPaid ? <>incassato{inv.paidDate ? ` ${ilGiorno(inv.paidDate)}` : ''}</>
                          : inRitardo ? <>{scadenzaRata(inv.dueDate)}: in ritardo da {durata(giorni!)}</> : scadenzaRata(inv.dueDate)}
                      </span>
                    </span>
                    <span className="fd-rata-cifra">{euro(inv.total)}</span>
                    <span className="fd-rata-act">
                      {aperta && canWrite && (
                        <span className="relative">
                          <button className="btn btn-sm" disabled={markInvoicePaid.isPending} onClick={() => setIncassoAperto(incassoAperto === 'tutta' ? null : 'tutta')}>
                            <Icon name="check" size={10} /> Segna incassata
                          </button>
                          {incassoAperto === 'tutta' && (
                            <SettlePopover
                              mode="incassa"
                              amount={Number(inv.total)}
                              subtitle={`fattura ${inv.number ?? ''}`}
                              counterparty={inv.client.legalName}
                              side="CREDIT"
                              adminData={conBanca}
                              withNote
                              avviso={AVVISO_INCASSO}
                              pending={markInvoicePaid.isPending}
                              onClose={() => setIncassoAperto(null)}
                              onConfirm={(v) => {
                                setIncassoAperto(null);
                                markInvoicePaid.mutate({ id: inv.id, total: Number(inv.total), label, ...v });
                              }}
                            />
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── 4. Cosa c'è in fattura ───────────────────────────────────── */}
          <section className="fd-blocco">
            <h2 className="fd-h2"><Icon name="list" size={15} /> Cosa c'è in fattura</h2>
            {inv.lines.length > 0 ? (
              <div className="fd-righe">
                <div className="fd-riga fd-riga--hd" aria-hidden>
                  <span>Descrizione</span><span>Quantità per prezzo</span><span>IVA</span><span>Con IVA</span>
                </div>
                {inv.lines.map((l) => {
                  const qty = Number(l.quantity) || 0;
                  const price = Number(l.unitPrice) || 0;
                  const vatRate = Number(l.vatRate) || 0;
                  return (
                    <div key={l.id} className="fd-riga">
                      <span className="fd-riga-desc">{l.description}</span>
                      <span className="mute">{qty} × {euro(price)}</span>
                      <span className="mute">{vatRate}%</span>
                      <span className="fd-riga-tot">{euro(qty * price * (1 + vatRate / 100))}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="fd-vuoto">{onFic ? 'Il dettaglio delle righe è su FattureInCloud: qui arriva solo il totale.' : 'Questa fattura non ha righe.'}</p>
            )}
            <dl className="fd-totali">
              <div><dt>Imponibile</dt><dd>{euro(inv.subtotal)}</dd></div>
              <div><dt>IVA</dt><dd>{euro(inv.vat)}</dd></div>
              <div className="is-tot"><dt>Totale con IVA</dt><dd>{euro(inv.total)}</dd></div>
            </dl>
          </section>

          {/* ── 5. Nota interna ─────────────────────────────────────────── */}
          <section className="fd-blocco">
            <h2 className="fd-h2"><Icon name="file" size={15} /> Nota interna <span className="fd-h2-sub">la vediamo solo noi</span></h2>
            {inv.notes ? <p className="fd-nota">{inv.notes}</p> : !notesOpen && <p className="fd-vuoto">Nessuna nota interna.</p>}
            {notesOpen ? (
              <>
                <textarea className="inp" rows={3} placeholder="Aggiungi una nota interna…" value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
                <div className="flex gap-2 mt-2">
                  <button className="btn btn-sm" disabled={!notes.trim() || updateNotes.isPending} onClick={() => updateNotes.mutate()}>
                    Salva la nota interna
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => { setNotesOpen(false); setNotes(''); }}>
                    Annulla
                  </button>
                </div>
              </>
            ) : canWrite && (
              <button className="btn btn-sm" onClick={() => { setNotes(inv.notes ?? ''); setNotesOpen(true); }}>
                <Icon name={inv.notes ? 'pencil' : 'plus'} size={12} /> {inv.notes ? 'Modifica la nota interna' : 'Aggiungi una nota interna'}
              </button>
            )}
          </section>
        </div>

        <aside className="fd-side">
          {/* ── Il cliente ─────────────────────────────────────────────── */}
          <section className="fd-scheda">
            <Link to={`/clients/${inv.client.id}`} className="fd-cliente">
              <BrandLogo name={inv.client.shortName || inv.client.legalName} size={40} />
              <span className="min-w-0">
                <span className="fd-cliente-t">{inv.client.legalName}</span>
                <span className="fd-cliente-s">Apri la scheda del cliente</span>
              </span>
              <Icon name="chevronR" size={12} className="mute" />
            </Link>
            <dl className="fd-dati">
              {inv.client.vat && <div><dt>Partita IVA</dt><dd className="mono">{inv.client.vat}</dd></div>}
              {inv.client.sdiCode
                ? <div><dt>Codice per la fattura elettronica</dt><dd className="mono">{inv.client.sdiCode}</dd></div>
                : inv.client.pec
                  ? <div><dt>PEC per la fattura elettronica</dt><dd className="mono">{inv.client.pec}</dd></div>
                  : <div><dt>Codice SDI o PEC</dt><dd className="text-alert">Mancano: servono per mandare la fattura elettronica</dd></div>}
            </dl>
          </section>

          {/* ── Da dove viene ──────────────────────────────────────────── */}
          {(quoteRef || inv.project || onFic) && (
            <section className="fd-scheda">
              <h2 className="fd-h3">Collegata a</h2>
              <div className="fd-link-lista">
                {quoteRef && (
                  <Link to={`/quotes/${quoteRef.id}`} className="fd-link">
                    <Icon name="file" size={14} /> <span className="min-w-0"><span className="t">Il preventivo {quoteRef.number ?? ''}</span><span className="s">da cui è nata la fattura</span></span>
                  </Link>
                )}
                {inv.project && (
                  <Link to={`/projects/${inv.project.id}`} className="fd-link">
                    <Icon name="cube" size={14} /> <span className="min-w-0"><span className="t">{inv.project.name}</span><span className="s">progetto {inv.project.code}</span></span>
                  </Link>
                )}
                {onFic && (
                  <a href={inv.ficUrl ?? undefined} target="_blank" rel="noreferrer" className="fd-link">
                    <Icon name="link" size={14} /> <span className="min-w-0"><span className="t">FattureInCloud, documento n. {inv.ficDocumentId}</span><span className="s">{inv.sdiId ? `numero SdI ${inv.sdiId}` : 'si apre in una nuova scheda'}</span></span>
                  </a>
                )}
              </div>
            </section>
          )}

          {/* ── Il percorso della fattura: i passi fatti spuntati, la frase solo su quello di adesso ── */}
          {!isCancelled && (
            <section className="fd-scheda">
              <h2 className="fd-h3">Il percorso della fattura</h2>
              <ol className="fd-passi">
                {PASSI.map((s, i) => {
                  const stato = i < passoAttuale ? 'fatto' : i === passoAttuale ? 'ora' : 'poi';
                  return (
                    <li key={s.key} className={`fd-passo is-${stato}${stato === 'ora' && inRitardo ? ' is-tardi' : ''}`}>
                      <span className="fd-passo-dot" aria-hidden>{stato !== 'poi' ? <Icon name="check" size={11} /> : null}</span>
                      <span className="min-w-0">
                        <span className="fd-passo-t">{s.titolo}{stato === 'ora' && <span className="fd-passo-qui">{inRitardo ? 'in ritardo col pagamento' : 'è qui'}</span>}</span>
                        {stato === 'ora' && <span className="fd-passo-s">{s.spiega(onFic)}</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </aside>
      </div>

      {emailModalOpen && (
        <SendEmailModal
          defaultTo=""
          invoiceNumber={inv.number ?? 'documento'}
          onClose={() => setEmailModalOpen(false)}
          onSend={(payload) => sendEmailFic.mutate(payload)}
          sending={sendEmailFic.isPending}
        />
      )}
    </div>
  );
}

/**
 * La risposta in una frase: se è pagata, quanto manca e da quando. Il rosso
 * solo sul ritardo. L'importo compare solo se ne manca una parte: se non ha
 * pagato niente, il totale sta già in "Cosa c'è in fattura".
 */
function risposta({ inv, chi, vive, aperte, residuo, giorni, scadenzaAperta }: {
  inv: InvoiceFull; chi: string; vive: RataFattura[]; aperte: RataFattura[]; residuo: number; giorni: number | null; scadenzaAperta: string | null;
}): ReactNode {
  if (inv.status === 'ANNULLATA') return <>Annullata: non vale più e non si può modificare.</>;
  if (inv.status === 'BOZZA') return <>È una <strong>bozza</strong> per {chi}: non ha ancora un numero e il cliente non l'ha ricevuta.</>;
  if (inv.status === 'PAGATA') {
    return <>{chi} l'ha <strong>pagata tutta</strong>{inv.paidDate ? <>: l'ultimo pagamento è arrivato {ilGiorno(inv.paidDate)}</> : null}.</>;
  }
  const pagate = vive.filter((p) => p.status === 'INCASSATA').length;
  const quale = vive.length > 1 && aperte[0] ? `la rata ${aperte[0].seq} di ${vive.length}` : 'il pagamento';
  // Quando arriva, detto dopo "manca la rata 1, che…" oppure dopo "la rata 1…".
  const quandoArriva: ReactNode = giorni == null ? <>non ha una scadenza</>
    : giorni < 0 ? <>doveva arrivare {ilGiorno(scadenzaAperta)}: <span className="is-alert">è in ritardo da {durata(giorni)}</span></>
    : giorni === 0 ? <>scade oggi</>
    : <>scade {ilGiorno(scadenzaAperta)}</>;
  if (pagate === 0) return <>{chi} <strong>non l'ha ancora pagata</strong>: {quale} {quandoArriva}.</>;
  const giaPagate = `${pagate === 1 ? 'una rata' : `${pagate} rate`} su ${vive.length}`;
  // Una rata sola da incassare: il suo importo sta sulla riga qui sotto, nella frase non si ripete.
  if (aperte.length <= 1) return <>{chi} ha già pagato {giaPagate}: manca <strong>{quale}</strong>, che {quandoArriva}.</>;
  return <>{chi} ha già pagato {giaPagate} e ci deve ancora <strong>{euro(residuo)}</strong>: {quale} {quandoArriva}.</>;
}

// ════════════════════════════════════════════════════════════════════
// SEND EMAIL MODAL (overlay leggero)
// ════════════════════════════════════════════════════════════════════
function SendEmailModal({ defaultTo, invoiceNumber, onClose, onSend, sending }: {
  defaultTo: string;
  invoiceNumber: string;
  onClose: () => void;
  onSend: (payload: { to?: string; cc?: string; subject?: string; body?: string }) => void;
  sending: boolean;
}) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`Fattura ${invoiceNumber}`);
  const [body, setBody] = useState('');

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 invoice-email-overlay" onClick={onClose}>
      <div className="bg-white rounded-[var(--r-card)] border border-line shadow-lg w-[460px] max-w-[92vw] invoice-email-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Spedisci la fattura">
        <div className="p-4 border-b border-line flex items-center justify-between invoice-email-hd">
          <div>
            <div className="t-micro">Invia al cliente · via FattureInCloud</div>
            <div className="t-h3 mt-0.5">Spedisci la fattura</div>
          </div>
          <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px] invoice-email-bd">
          <div>
            <label className="field-label">Destinatario</label>
            <input className="inp" placeholder="Lascia vuoto per usare il contatto primario del cliente" value={to} onChange={(e) => setTo(e.target.value)} />
            <div className="field-hint">Se lo lasci vuoto, FattureInCloud usa la mail del contatto principale.</div>
          </div>
          <div>
            <label className="field-label">In copia</label>
            <input className="inp" placeholder="Facoltativo" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Oggetto</label>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Messaggio</label>
            <textarea className="inp" rows={4} placeholder="Lascia vuoto per usare il testo predefinito di FattureInCloud" value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t border-line flex gap-2 invoice-email-ft">
          <button className="btn" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={sending}
            onClick={() => onSend({
              to: to.trim() || undefined,
              cc: cc.trim() || undefined,
              subject: subject.trim() || undefined,
              body: body.trim() || undefined,
            })}
          >
            <Icon name="send" size={12} /> {sending ? 'Invio in corso…' : 'Manda la fattura'}
          </button>
        </div>
      </div>
    </div>
  );
}
